'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import type { ProcessedTheme } from '../../ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses, compactDateNumeric } from '../../ProcessedEntry/DomainEntry/processedTypes'
import type { Catalog, PipelineStep } from '../../Entries/entryTypes'
import { SubmissionChatView } from '../../Entries/SubmissionChatView'
import { FeedPreviewCard } from '../../Entries/FeedPreviewCard'
import {
  SURFACES,
  detectSurface,
  readOriginalPayload,
  selectAnswer,
  stepsForAnswer,
  surfaceInfo,
  type AnswerMoment,
  type PublicAnswer,
  type PublicSurface,
} from './model'
import {
  buildBonResultMessages,
  formatElegitimoVerdict,
  formatDomainVerdict,
  validatorMessages,
  validatorError,
  ELEGITIMO_STATES,
  DOMAIN_SUBMITTING,
} from './ports'
import { ChatBubbles, VerdictBox, ResultList, PreviewNote } from './renderers'

// "What the user sees": the answer this entry's reporter got, rendered the way
// their public surface rendered it, with tabs to see the same entry through
// every other surface and a toggle between the answer at submit time and what
// the surface (or its replay view) shows now.
//
// Props-driven, not self-fetching: CaseEntryDetailPage already loads the
// pipeline steps for this entry on mount and on Refresh (handleRefresh), and
// RawProcessedEntryPanel fetches the same endpoint a second time — a third
// fetch would only make this tile lag the Refresh button and the Pipeline
// Steps tile beside it. `stepsReady` is false until that first load lands,
// because list rows arrive with user_entry_pipeline_steps: [] and an empty
// array would otherwise read as "the entry has no verdict".

type Props = {
  theme: ProcessedTheme
  catalog: Catalog
  /** raw_entries row (processed fields may be layered over it by the caller). */
  raw: Record<string, any>
  /** processed_entries row, for the feed card's public fields. */
  processed?: Record<string, any> | null
  steps: PipelineStep[]
  stepsReady: boolean
  /** user_entries.source (operator-editable); raw.source is the fallback. */
  source?: string | null
  userEntryId?: string
  defaultMoment?: AnswerMoment
  className?: string
}

const MOMENTS: { id: AnswerMoment; label: string; title: string }[] = [
  { id: 'submit', label: 'À submissão', title: 'O que o utilizador viu no momento em que submeteu' },
  { id: 'now', label: 'Agora', title: 'O que a superfície (ou a sua vista de histórico) mostra hoje' },
]

export function UserFacingPreviewWidget({ theme, catalog, raw, processed, steps, stepsReady, source, userEntryId, defaultMoment = 'now', className }: Props) {
  const c = watchClasses(theme)
  const payload = useMemo(() => readOriginalPayload(raw), [raw])
  const effectiveSource = source ?? (raw?.source as string | undefined) ?? null
  const detection = useMemo(() => detectSurface(effectiveSource, payload), [effectiveSource, payload])
  // A machine-sourced entry has no end user; the feed card is the one public
  // place such an entry can appear once accepted, so that tab is the default.
  const defaultTab: Exclude<PublicSurface, 'none'> = detection.surface === 'none' ? 'feed-card' : detection.surface

  const [surface, setSurface] = useState<Exclude<PublicSurface, 'none'>>(defaultTab)
  const [moment, setMoment] = useState<AnswerMoment>(defaultMoment)
  // Follow the detected surface when the entry (or its source) changes.
  useEffect(() => { setSurface(defaultTab) }, [defaultTab, userEntryId])

  const answer = useMemo(() => selectAnswer(steps, raw, payload, surface, moment), [steps, raw, payload, surface, moment])
  const machineSource = detection.surface === 'none'
  const info = surfaceInfo(surface)

  return (
    <div
      className={`flex h-full min-h-0 flex-col ${className ?? ''}`}
      data-user-facing-preview
      data-user-entry-id={userEntryId}
      data-detected-surface={detection.surface}
      data-surface={surface}
      data-moment={moment}
      data-answer-kind={answer.kind}
      data-answer-step={answer.step?.step}
      data-answer-is-scam={answer.isScam === undefined ? undefined : String(answer.isScam)}
    >
      {/* Toolbar: surface tabs + moment toggle */}
      <div className={`flex shrink-0 flex-wrap items-center gap-1 border-b px-2 py-1.5 ${c.line}`}>
        <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Superfície pública">
          {SURFACES.map((s) => {
            const active = s.id === surface
            const detected = s.id === detection.surface
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-pressed={active}
                data-surface-tab={s.id}
                data-detected={detected || undefined}
                title={`${s.title}${detected ? ` — detetada (${detection.evidence.join('; ')})` : ''}\nEspelha: ${s.upstream.join(' · ')}`}
                onClick={() => setSurface(s.id)}
                className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] transition ${
                  active
                    ? 'border-signal bg-signal-surface text-signal'
                    : `${c.line} ${c.muted} hover:border-signal/40`
                }`}
              >
                {detected && <span className="h-1.5 w-1.5 rounded-full bg-signal" aria-label="detetada" />}
                {s.label}
                {s.id === 'validator' && detection.variant && detected && <span className={`text-[10px] ${c.faint}`}>· {detection.variant}</span>}
              </button>
            )
          })}
        </div>
        <div className={`ml-auto inline-flex overflow-hidden rounded-md border text-[11px] ${c.line}`} role="group" aria-label="Momento">
          {MOMENTS.map((m) => (
            <button
              key={m.id}
              type="button"
              data-moment-toggle={m.id}
              aria-pressed={moment === m.id}
              title={m.title}
              onClick={() => setMoment(m.id)}
              className={`px-2 py-0.5 ${moment === m.id ? 'bg-signal-surface text-signal' : `${c.muted} hover:bg-secondary`}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Body: the surface's rendering, phone-width */}
      <div className="min-h-0 flex-1 overflow-y-auto px-3">
        <div className="mx-auto w-full max-w-[420px]">
          {!stepsReady ? (
            <div className={`flex items-center gap-2 py-6 text-[11px] ${c.faint}`}>
              <Loader2 className="h-4 w-4 animate-spin text-signal" /> A carregar os passos da classificação…
            </div>
          ) : (
            <>
              {machineSource && (
                <PreviewNote theme={theme} tone="warning">
                  Fonte <span className="font-mono">{effectiveSource ?? '—'}</span>: nenhum utilizador recebeu resposta a esta entrada. Os separadores
                  mostram como ficaria em cada superfície.
                </PreviewNote>
              )}
              <SurfaceBody surface={surface} answer={answer} theme={theme} catalog={catalog} raw={raw} processed={processed} steps={steps} />
            </>
          )}
        </div>
      </div>

      {/* Provenance strip */}
      <div className={`shrink-0 border-t px-3 py-1.5 font-mono text-[10px] leading-4 ${c.line} ${c.faint}`} data-answer-provenance>
        <Provenance answer={answer} info={info?.title} detection={detection.confidence === 'default' ? `superfície assumida: ${detection.evidence.join('; ')}` : undefined} />
      </div>
    </div>
  )
}

// ─── Per-surface body ─────────────────────────────────────────────────────────

function SurfaceBody({
  surface, answer, theme, catalog, raw, processed, steps,
}: {
  surface: Exclude<PublicSurface, 'none'>
  answer: PublicAnswer
  theme: ProcessedTheme
  catalog: Catalog
  raw: Record<string, any>
  processed?: Record<string, any> | null
  steps: PipelineStep[]
}) {
  switch (surface) {
    case 'bon-chat': {
      // Card1Chat decides from the response object: a pending response (or a
      // failed classification, which Hono reports as classificationPending)
      // and a call both go through the same builder.
      const result = answer.kind === 'answer' ? answer.json : answer.kind === 'pending' || answer.kind === 'failed' ? { classificationPending: true } : null
      return <ChatBubbles messages={buildBonResultMessages(result, raw?.post_type)} theme={theme} />
    }
    case 'bon-my-submissions': {
      if (answer.kind !== 'answer' && steps.length === 0) {
        return <PreviewNote theme={theme}>Sem passos de classificação: a conversa termina em “Submetido”.</PreviewNote>
      }
      return (
        <SubmissionChatView
          submission={{ raw_entries: raw, pipeline_steps: stepsForAnswer(steps, answer), posted: true }}
          catalog={catalog}
          theme={theme}
        />
      )
    }
    case 'elegitimo': {
      const verdict =
        answer.kind === 'answer'
          ? (formatElegitimoVerdict(answer.json) ?? ELEGITIMO_STATES.doneNoVerdict)
          : answer.kind === 'failed'
            ? ELEGITIMO_STATES.failed
            : answer.moment === 'submit' && answer.notes.some((n) => n.includes('timeout'))
              ? ELEGITIMO_STATES.timeout
              : ELEGITIMO_STATES.submitting
      return <VerdictBox verdict={verdict} theme={theme} variant="elegitimo" />
    }
    case 'validator': {
      // Exactly what Hono hands the validator: { success, data }.
      const wire = answer.kind === 'answer' ? { success: true, data: answer.json } : answer.kind === 'failed' ? { success: false, error: 'A validação falhou.' } : null
      return <ResultList messages={wire ? validatorMessages(wire) : []} error={wire ? validatorError(wire) : null} theme={theme} waiting={answer.kind === 'pending'} />
    }
    case 'domain-checker': {
      if (answer.kind !== 'answer') return <VerdictBox verdict={answer.kind === 'pending' ? formatDomainVerdict({ classificationPending: true })! : DOMAIN_SUBMITTING} theme={theme} variant="domain-checker" />
      const verdict = formatDomainVerdict(answer.json)
      if (!verdict) return <PreviewNote theme={theme}>O verificador não mostra caixa de veredicto para este resultado (sem is_scam, sem sinais).</PreviewNote>
      return <VerdictBox verdict={verdict} theme={theme} variant="domain-checker" />
    }
    case 'feed-card': {
      // The feed shows the processed (accepted) fields; the badge follows the
      // answer's verdict for the chosen moment, not the row's possibly-overwritten is_scam.
      const entry = { ...raw, ...(processed ?? {}), is_scam: answer.isScam ?? null }
      return (
        <div className="py-2" data-preview-body="feed-card">
          <FeedPreviewCard entry={entry} catalog={catalog} theme={theme} />
        </div>
      )
    }
    default:
      return null
  }
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Provenance({ answer, info, detection }: { answer: PublicAnswer; info?: string; detection?: string }) {
  const parts: string[] = []
  if (info) parts.push(info)
  if (answer.step) {
    parts.push(`${answer.step.step} · ${compactDateNumeric(answer.step.created_at)}`)
    if (answer.secondsAfterSubmit != null) parts.push(`+${answer.secondsAfterSubmit}s`)
    if (answer.deliveredSync === true) parts.push('síncrono')
    if (answer.deliveredSync === false) parts.push('não síncrono')
  } else {
    parts.push(
      answer.kind === 'pending' ? 'sem veredicto no momento'
      : answer.kind === 'failed' ? 'classificação falhou'
      : answer.kind === 'call' ? 'chamada: sem classificação'
      : answer.kind === 'none' ? 'sem utilizador final'
      : '—',
    )
  }
  if (answer.hidden) parts.push('hidden')
  if (answer.hideClassification) parts.push('hide_classification')
  if (detection) parts.push(detection)
  // Product gaps the port reproduces, worth a word so nobody "fixes" the widget.
  const gaps: string[] = []
  if (answer.surface === 'validator' && answer.kind === 'answer' && typeof answer.json.beneficiario === 'string' && !answer.json.bk_result) {
    gaps.push('a app lê bk_result/payments_metadata; o resultado guarda beneficiario no topo, por isso "Destinatário indicado" não aparece')
  }
  if (answer.surface === 'domain-checker') gaps.push('a captura do site vem de outra API e não é reproduzida')
  return (
    <>
      <div className="truncate" title={parts.join(' · ')}>{parts.join(' · ')}</div>
      {[...answer.notes, ...gaps].map((n, i) => (
        <div key={i} className="truncate" title={n}>— {n}</div>
      ))}
    </>
  )
}
